import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBIcon,
} from "mdb-react-ui-kit";
import VoteTable from "../../../helper/VoteTable";
import jsPDF from "jspdf";

const VoteTabulationModal = ({
  floorVoteData,
  voteData,
  bill,
  activeSessionInfo,
  showVoteTabulationUI,
  setShowVoteTabulationUI,
}) => {
  const [votes, setVotes] = useState({
    yes: [],
    yes_excused: [],
    no: [],
    no_excused: [],
    excused: [],
    absent: [],
    yes_by_proxy: [],
    no_by_proxy: [],
    no_vote: []
  });
  const [legislatorDetails, setLegislatorDetails] = useState({});
  const matchingRecord = voteData.find(
    (record) => record.billStatusId === showVoteTabulationUI.billStatusId
  );

  const matchingFloorVoteRecord = floorVoteData.find(
    (record) => record.billStatus?.id === showVoteTabulationUI.billStatusId
  );
  const [sponsor, setSponsor] = useState(null);
  const [sessionInfoForHeader, setSessionInfoForHeader] =
    useState("Loading...");

  const getSponsor = async () => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators/${bill.sponsorId}`
    );
    return data;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
      timeZone: "America/Denver",
    });
  };

  useEffect(() => {
    const fetchSponsor = async () => {
      try {
        const sponsorData = await getSponsor();
        setSponsor(sponsorData);
      } catch (error) {
        console.error("Error fetching sponsor:", error);
      }
    };

    fetchSponsor();
  }, [bill.sponsorId]);

  const fetchLegislatorDetails = async (legislatorIds) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators/search?limit=9999&offset=0`,
        {
          legislatorIds: legislatorIds,
          sessionIds: [activeSessionInfo.id],
        }
      );
      const legislatorsData = response.data.content.reduce(
        (acc, legislator) => {
          acc[legislator.id] = legislator;
          return acc;
        },
        {}
      );
      setLegislatorDetails(legislatorsData);
    } catch (error) {
      console.error("Error fetching legislator details:", error);
    }
  };

  useEffect(() => {
    if (matchingRecord) {
      const sortedVotes = matchingRecord.legislatorVotes.reduce(
        (acc, vote) => {
          const category = vote.committeeVote.toLowerCase();
          if (
            [
              "yes",
              "yes_excused",
              "no",
              "no_excused",
              "excused",
              "absent",
              "yes_by_proxy",
              "no_by_proxy",
              "no_vote"
            ].includes(category)
          ) {
            acc[category].push(vote.membership.legislatorId);
          }
          return acc;
        },
        {
          yes: [],
          yes_excused: [],
          no: [],
          no_excused: [],
          excused: [],
          absent: [],
          yes_by_proxy: [],
          no_by_proxy: [],
          no_vote: []
        }
      );
      setVotes(sortedVotes);

      const allLegislatorIds = [
        ...sortedVotes.yes,
        ...sortedVotes.yes_excused,
        ...sortedVotes.no,
        ...sortedVotes.no_excused,
        ...sortedVotes.excused,
        ...sortedVotes.absent,
        ...sortedVotes.yes_by_proxy,
        ...sortedVotes.no_by_proxy,
        ...sortedVotes.no_vote
      ];
      fetchLegislatorDetails(allLegislatorIds);
    }
  }, [matchingRecord]);

  useEffect(() => {
    if (matchingFloorVoteRecord) {
      const sortedFloorVotes = matchingFloorVoteRecord.legislatorVotes.reduce(
        (acc, vote) => {
          const category = vote.voteType.toLowerCase();
          if (
            [
              "yes",
              "yes_excused",
              "no",
              "no_excused",
              "excused",
              "absent",
              "yes_by_proxy",
              "no_by_proxy",
              "no_vote"
            ].includes(category)
          ) {
            acc[category].push(vote.legislatorId);
          }
          return acc;
        },
        {
          yes: [],
          yes_excused: [],
          no: [],
          no_excused: [],
          excused: [],
          absent: [],
          yes_by_proxy: [],
          no_by_proxy: [],
          no_vote: []
        }
      );
      setVotes(sortedFloorVotes);
      const allLegislatorIds = [
        ...sortedFloorVotes.yes,
        ...sortedFloorVotes.yes_excused,
        ...sortedFloorVotes.no,
        ...sortedFloorVotes.no_excused,
        ...sortedFloorVotes.excused,
        ...sortedFloorVotes.absent,
        ...sortedFloorVotes.yes_by_proxy,
        ...sortedFloorVotes.no_by_proxy,
        ...sortedFloorVotes.no_vote
      ];
      fetchLegislatorDetails(allLegislatorIds);
    }
  }, [matchingFloorVoteRecord]);

  useEffect(() => {
    setSessionInfoForHeader(
      `${new Date(activeSessionInfo.startDate).getFullYear().toString()} (${
        activeSessionInfo.type
      })`
    );
  }, [activeSessionInfo]);

  const getCommitteeName = () => {
    if (matchingRecord?.standingCommitteeMeeting) {
      return (
        matchingRecord.standingCommitteeMeeting.standingCommittee
          .committeeDetails.committeeCode.name || "N/A"
      );
    } else if (
      matchingFloorVoteRecord?.billStatus?.billStatusCode?.chamber === "SENATE"
    ) {
      return "(S) Committee of the Whole";
    } else if (
      matchingFloorVoteRecord?.billStatus?.billStatusCode?.chamber === "HOUSE"
    ) {
      return "(H) Committee of the Whole";
    } else {
      return "N/A";
    }
  };

  const generatePDF = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const margin = 36;
    const bottomMargin = 60; // Space above footer to prevent content overlap
    const pageWidth = doc.internal.pageSize.width;
    const logoPath = "/logo192.png";
    const reportTitle = `${bill?.billType?.code || ""} ${
      bill?.billNumber
    } Vote History`;

    const convertToBase64 = async (path) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = path;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
      });
    };

    const logoBase64 = await convertToBase64(logoPath);
    const logoWidth = 40;
    const logoHeight = 40;

    doc.addImage(logoBase64, "PNG", margin, 20, logoWidth, logoHeight);

    // Add Session Title (centered)
    doc.setFontSize(12);

    function formatLegislatureSession(activeSessionInfo) {
      if (!activeSessionInfo || !activeSessionInfo.legislature) {
        return "Legislature Session Information Not Available";
      }

      const ordinal = activeSessionInfo.legislature.ordinals;
      const startDate = activeSessionInfo.legislature.startDate;

      if (!ordinal || !startDate) {
        return "Legislature Session Information Incomplete";
      }

      const year = new Date(startDate).getFullYear();

      return `${ordinal}th Legislature ${year} Session`;
    }
    const formattedSession = formatLegislatureSession(activeSessionInfo);

    doc.text(formattedSession, pageWidth / 2, margin, {
      align: "center",
    });

    let yPos = margin + 20; // Start below the logo and title

    // Add Report Title (centered)
    doc.setFont("helvetica");
    doc.setFontSize(16);
    const titleWidth = doc.getTextWidth(reportTitle);
    const titleX = (doc.internal.pageSize.width - titleWidth) / 2;
    doc.text(reportTitle, titleX, yPos);
    yPos += 30;

    // Format Top Details in Two Columns
    const leftColumnX = margin;
    const rightColumnX = margin + 240; // Align second column further right

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Vote Date & Time:", leftColumnX, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(
      matchingFloorVoteRecord?.dateTime
        ? formatDate(matchingFloorVoteRecord.dateTime)
        : matchingRecord?.voteTime
        ? formatDate(matchingRecord.voteTime)
        : "Vote time not available",
      leftColumnX + 100,
      yPos
    );

    doc.setFont("helvetica", "bold");
    doc.text("Committee:", rightColumnX, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(getCommitteeName(), rightColumnX + 70, yPos);
    yPos += 20;

    doc.setFont("helvetica", "bold");
    doc.text("Motion:", leftColumnX, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(
      (matchingFloorVoteRecord?.motion || matchingRecord?.motion || "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      leftColumnX + 100,
      yPos
    );

    // Calculate Total Vote Counts
    const formattedVotes = Object.entries(votes).flatMap(
      ([voteType, legislatorIds]) =>
        legislatorIds
          .map((id) => legislatorDetails[id])
          .filter(Boolean)
          .sort((a, b) => a.lastName.localeCompare(b.lastName))
          .map((legislator) => ({
            name: `${legislator.lastName}, ${legislator.firstName}`,
            party: `${legislator.politicalParty.code}`,
            district:
              legislator.chamber === "House"
                ? `HD-${legislator.district.id}`
                : `SD-${legislator.district.id}`,
            voteType: voteType.charAt(0).toUpperCase() + voteType.slice(1), // Capitalize first letter
            isExcused: voteType.includes("excused") || voteType.includes("by_proxy"),
          }))
    );
    
    const yesVotes = formattedVotes.filter((vote) => vote.voteType.startsWith("Yes"));
    const noVotes = formattedVotes.filter((vote) => vote.voteType.startsWith("No"));

    const totalYes = yesVotes.length;
    const totalNo = noVotes.length;
    // const totalVotes =
    //   totalYes + totalNo + votes.excused.length + votes.absent.length;
    // doc.setFont("helvetica", "bold");
    // doc.text("Total Votes:", rightColumnX, yPos);
    // doc.setFont("helvetica", "normal");
    // doc.text(`${totalVotes}`, rightColumnX + 70, yPos);
    yPos += 30;

    // Function to add footer on every page
    const addFooter = () => {
      const today = new Date();
      const timeString = today.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
      const dateString = today.toLocaleDateString("en-US");

      const footerText = `${reportTitle} - Created ${dateString} at ${timeString} - Page ${
        doc.internal.getCurrentPageInfo().pageNumber
      } of ${doc.internal.getNumberOfPages()}`;

      const footerX = doc.internal.pageSize.width / 2;
      const footerY = doc.internal.pageSize.height - 20;

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(footerText, footerX, footerY, { align: "center" });
    };

    // Add Footer on First Page Before Moving On
    addFooter();

    // Helper function to check page space
    const checkAndAddPage = (sectionHeight) => {
      if (yPos + sectionHeight > doc.internal.pageSize.height - bottomMargin) {
        addFooter(); // Add footer before new page
        doc.addPage();
        yPos = margin + 30; // Reset yPos for the new page
      }
    };

    // Add column titles
    doc.setFont("helvetica", "bold");
    doc.text(`YES (${totalYes})`, leftColumnX, yPos);
    doc.text(`NO (${totalNo})`, rightColumnX, yPos);
    yPos += 15;

    // Loop through votes and add them side by side
    const maxVotes = Math.max(yesVotes.length, noVotes.length);

    doc.setFont("helvetica", "normal");
    for (let i = 0; i < maxVotes; i++) {
      checkAndAddPage(20); // Ensure space for new row

      // YES Votes Column
      if (yesVotes[i]) {
        const yesVote = yesVotes[i];
        doc.text(
          `${yesVote.name} (${yesVote.party}) ${yesVote.district}${
            yesVote.isExcused ? " *" : ""
          }`,
          leftColumnX,
          yPos
        );
      }

      // NO Votes Column
      if (noVotes[i]) {
        const noVote = noVotes[i];
        doc.text(
          `${noVote.name} (${noVote.party}) ${noVote.district}${
            noVote.isExcused ? " *" : ""
          }`,
          rightColumnX,
          yPos
        );
      }

      yPos += 15;
    }

    // Final Footer Before Saving
    addFooter();

    // Save the PDF
    doc.save(`${bill?.billType?.code}${bill?.billNumber}-VoteHistory.pdf`);
  };

  return (
    <MDBModal
      show={showVoteTabulationUI.show}
      onClose={() =>
        setShowVoteTabulationUI({ show: false, billStatusId: null })
      }
      tabIndex="-1"
    >
      <MDBModalDialog size="xl" className="modal-dialog-centered">
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle className="w-100">
              {bill?.billType?.code || ""} {bill?.billNumber} Vote History
            </MDBModalTitle>
            <MDBBtn className="text-nowrap me-2" onClick={generatePDF}>
              <MDBIcon fas icon="print" /> Print
            </MDBBtn>
            <MDBBtn
              className="btn-close"
              color="none"
              onClick={() =>
                setShowVoteTabulationUI({ show: false, billStatusId: null })
              }
            ></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody className="p-4" id="printableArea">
            <div className="row">
              <div className="col-4">
                <h6 className="fw-bold">Vote Date & Time</h6>
                <p>
                  {matchingFloorVoteRecord?.dateTime
                    ? formatDate(matchingFloorVoteRecord.dateTime)
                    : matchingRecord?.voteTime
                    ? formatDate(matchingRecord.voteTime)
                    : "Vote time not available"}
                </p>
              </div>
              <div className="col-4">
                <h6 className="fw-bold">Committee</h6>
                <p>{getCommitteeName()}</p>
              </div>
              <div className="col-4">
                <h6 className="fw-bold">Motion</h6>
                <p>
                  {(
                    matchingFloorVoteRecord?.motion ||
                    matchingRecord?.motion ||
                    ""
                  )
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </p>
              </div>
            </div>
            <VoteTable
              votes={Object.fromEntries(
                Object.entries(votes).map(([key, value]) => [
                  key.charAt(0).toUpperCase() + key.slice(1), // Capitalize key
                  value,
                ])
              )}
              legislatorDetails={legislatorDetails}
            />
          </MDBModalBody>
          <MDBModalFooter className="bg-light d-flex justify-content-between align-items-center">
            <small>
              <strong>*</strong> = By Proxy
            </small>
            <div>
              <MDBBtn
                color="dark"
                onClick={() =>
                  setShowVoteTabulationUI({ show: false, billStatusId: null })
                }
              >
                Close
              </MDBBtn>
              <MDBBtn onClick={generatePDF} className="ms-2">
                <MDBIcon fas icon="print" /> Print
              </MDBBtn>
            </div>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
};

export default VoteTabulationModal;
